import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { portalAPI } from '../../services/api';
import { reportLoadError } from '../../utils/notify';
import { toLocalISODate } from '../../utils/dateUtils';
import StatusBadge, { statusVariant } from '../../components/StatusBadge';

// The guard's own attendance, a month at a time, read only (HR records it).

const STATUS_LABEL = { present: 'Present', late: 'Present', approved: 'Leave', leave: 'Leave', vacation: 'Leave', absent: 'Absent', sick: 'Sick' };
const shiftMonth = (month, n) => {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return d.toISOString().slice(0, 7);
};

const PortalAttendance = () => {
  const { t, locale } = useLanguage();
  const [month, setMonth] = useState(() => toLocalISODate().slice(0, 7));
  const [days, setDays] = useState(null);
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    let cancelled = false;
    setDays(null);
    portalAPI.attendance(month)
      .then((res) => { if (!cancelled) setDays(res.data.days); })
      .catch((error) => reportLoadError(error, tRef.current('Error loading your attendance'), { main: true }));
    return () => { cancelled = true; };
  }, [month]);

  const worked = (days || []).filter((d) => ['present', 'late'].includes(d.status) || (d.status === 'approved' && !d.leave_type_name));
  const hours = worked.reduce((sum, d) => sum + (Number(d.hours_worked) || 0), 0);
  const monthLabel = new Date(`${month}-15T12:00:00`).toLocaleDateString(locale, { month: 'long', year: 'numeric' });

  return (
    <div className="pt-page">
      <h1 className="pt-title">{t('My attendance')}</h1>
      <div className="pt-month">
        <button type="button" className="btn btn--secondary btn--icon" aria-label={t('Previous month')} onClick={() => setMonth(shiftMonth(month, -1))}><ChevronLeft aria-hidden="true" /></button>
        <span className="pt-month__label">{monthLabel}</span>
        <button type="button" className="btn btn--secondary btn--icon" aria-label={t('Next month')} onClick={() => setMonth(shiftMonth(month, 1))}
          disabled={month >= toLocalISODate().slice(0, 7)}><ChevronRight aria-hidden="true" /></button>
      </div>
      {!days ? <p className="pt-empty">{t('Loading...')}</p> : (
        <>
          <p className="pt-summary">{t('{days} days worked · {hours} h', { days: worked.length, hours: Math.round(hours * 100) / 100 })}</p>
          {days.length === 0 && <p className="pt-empty">{t('Nothing recorded this month')}</p>}
          <ul className="pt-list">
            {days.map((d) => (
              <li key={d.date} className="pt-card pt-row">
                <div>
                  <div className="pt-row__title">{new Date(`${d.date}T12:00:00`).toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' })}</div>
                  <div className="pt-muted">
                    {d.check_in_time ? `${d.check_in_time} – ${d.check_out_time || '…'}` : (d.leave_type_name || '')}
                    {d.holiday_name ? ` · ${d.holiday_name}` : ''}
                  </div>
                  {d.is_late && d.late_minutes > 0 && <div className="pt-flag">{t('Late {minutes} min', { minutes: d.late_minutes })}</div>}
                </div>
                <div className="pt-row__end">
                  <StatusBadge variant={statusVariant(d.status)}>{d.leave_type_name || t(STATUS_LABEL[d.status] || d.status)}</StatusBadge>
                  {d.hours_worked != null && <span className="pt-muted num">{Number(d.hours_worked).toFixed(1)} h</span>}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

export default PortalAttendance;
